import "./TenderAnalysis.scss";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CustomDropdown from "../../Common/CustomDropdown/CustomDropdown.js";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import SupplierBidCard from "./SupplierBidCard.js";
// @ts-ignore
import { formatCost, roundToTwo } from "../../../utils/utils.js";
import { useNegotiationsStore, SourcingProductOption } from "../../../stores/negotiationsStore";
import CheckIcon from "../../../assets/images/CheckIcon.js";
import WarningIcon from "../../../assets/images/WarningIcon.js";
import CrossIcon from "../../../assets/images/CrossIcon.js";

// Helper to format product options for dropdown
interface ProductDropdownOption {
	name: string;
	code: string;  // variant_id is used as the code/key
	variant_id: string | null;
	display: string | null;
	display_description: string | null;
}

// Helper to format country options for dropdown
interface CountryDropdownOption {
	name: string;
	code: string;
}

const TenderAnalysis = () => {
	const { negotiation_id } = useParams<{ negotiation_id?: string }>();
	const negId = negotiation_id ? parseInt(negotiation_id, 10) : undefined;
	const [selectedProduct, setSelectedProduct] = useState<ProductDropdownOption | null>(null);
	const [selectedCountry, setSelectedCountry] = useState<CountryDropdownOption | null>(null);
	const {
		sourcingProducts,
		countryLists,
		productBidSummary,
		fetchSourcingProducts,
		fetchCountryLists,
		fetchProductBidSummary,
		clearSourcingProductData,
	} = useNegotiationsStore();

	const { product, bid_overview, specification_gaps } = productBidSummary || {};

	// Group bid_overview by sourcing_country
	const bidsByCountry = (bid_overview || []).reduce((acc: Record<string, any[]>, bid: any) => {
		const country = bid.sourcing_country || "Unknown";
		if (!acc[country]) {
			acc[country] = [];
		}
		acc[country].push(bid);
		return acc;
	}, {});

	// Get sorted country keys
	const sortedCountries = Object.keys(bidsByCountry).sort();

	const formatTenderPrice = (value: any) => {
		const num = Number(value);
		if (Number.isNaN(num)) return "-";
		if (num === 0) return "0";
		return num.toFixed(4);
	};

	// Transform sourcingProducts to dropdown format (using variant_id as key)
	const productDropdownOptions: ProductDropdownOption[] = sourcingProducts.map((item: SourcingProductOption) => ({
		name: item.product_description,
		code: item.variant_id || item.product_description,  // Use variant_id as the identifier
		variant_id: item.variant_id,
		display: item.display,
		display_description: item.display_description,
	}));

	// Transform countryLists to dropdown format with "All Countries" option
	const countryDropdownOptions: CountryDropdownOption[] = [
		{ name: "All Countries", code: "" },
		...countryLists.map((country: string) => ({
			name: country,
			code: country,
		})),
	];

	// Helper to build product details for a specific country
	const getProductDetails = (country: string) => [
		{
			key: "Product",
			value: product?.product_description || "-",
		},
		{
			key: "Variant ID",
			value: product?.variant_id || "-",
		},
		{
			key: "Display",
			value: product?.display_description || product?.display || "-",
		},
		{
			key: "Country",
			value: country,
		},
	];

	// Handle product selection change
	const handleProductChange = (selected: ProductDropdownOption) => {
		setSelectedProduct(selected);
		setSelectedCountry(null); // Reset country when product changes
	};

	// Handle country selection change
	const handleCountryChange = (selected: CountryDropdownOption) => {
		setSelectedCountry(selected);
	};

	// Fetch products on mount
	useEffect(() => {
		if (negId) {
			fetchSourcingProducts(negId);
		}
		return () => {
			clearSourcingProductData();
		};
	}, [negId]);

	// Auto-select first product when list is fetched
	useEffect(() => {
		if (productDropdownOptions.length > 0 && !selectedProduct) {
			setSelectedProduct(productDropdownOptions[0]);
		}
	}, [sourcingProducts]);

	// Fetch countries when product changes
	useEffect(() => {
		if (negId && selectedProduct?.variant_id) {
			fetchCountryLists(negId, selectedProduct.variant_id);
		}
	}, [negId, selectedProduct]);

	// Fetch bid summary when product or country changes
	useEffect(() => {
		if (negId && selectedProduct?.variant_id) {
			const countryFilter = selectedCountry?.code || undefined;
			fetchProductBidSummary(negId, selectedProduct.variant_id, countryFilter);
		}
	}, [negId, selectedProduct, selectedCountry]);

	return (
		<div className="main-wrapper" style={{ width: "100%" }}>
			{/* Executive Summary */}
			<div className="card_header">
				<h3 className="card_title my-0">Executive Summary</h3>
			</div>
			{/* Product and Country Dropdowns */}
			<div className="dropdown-wrapper">
				<div className="filter-content" style={{ width: "400px" }}>
					<p className="filter-label my-0">Select Product*</p>
					<CustomDropdown
						placeholder="Select Product"
						options={productDropdownOptions}
						selectedOption={selectedProduct}
						onSelect={handleProductChange}
					/>
				</div>
				<div className="filter-content" style={{ width: "250px" }}>
					<p className="filter-label my-0">Country</p>
					<CustomDropdown
						placeholder="All Countries"
						options={countryDropdownOptions}
						selectedOption={selectedCountry || countryDropdownOptions[0]}
						onSelect={handleCountryChange}
					/>
				</div>
			</div>
			{/* Bid Overview */}
			<div className="card_header">
				<h3 className="card_title my-0">Bid Overview</h3>
			</div>
			{/* Placeholder when no product selected */}
			{!selectedProduct && (
				<div className="placeholder-state" style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
					<p>Please select a product to view bid overview.</p>
				</div>
			)}
			{/* Product Details + KPI Cards grouped by Country */}
			{selectedProduct && productBidSummary && (
				<>
					{sortedCountries.length > 0 ? (
						sortedCountries.map((country) => (
							<div key={country} className="country-bid-group">
								{/* Product Details for this country */}
								<div className="product_wrapper">
									<div className="header_container">
										<h1 className="my-0">
											{product?.product_description?.toUpperCase() || "-"}
										</h1>
									</div>
									<div className="article-container">
										{getProductDetails(country).map((item) => (
											<div className="articles" key={item.key}>
												<h1 className="my-0">{item.key}:</h1>
												<p className="my-0">{item.value}</p>
											</div>
										))}
									</div>
								</div>
								{/* Bid Cards for this country */}
								<div className="bid-cards-wrapper">
									<div className="kpi-cards w-full">
										{bidsByCountry[country].map((ele: any) => (
											<SupplierBidCard
												key={`${ele.supplier_id}-${country}`}
												supplierId={ele.supplier_id}
												supplierName={ele.supplier_name}
												sampleQuality={Number(ele.product_quality) || 0}
												currentBid={formatTenderPrice(ele.current_bid)}
												targetBid={formatTenderPrice(ele.target_price)}
												gap={{
													price: formatTenderPrice(ele.price_gap),
												}}
												incoterm={ele.incoterm}
											/>
										))}
									</div>
								</div>
							</div>
						))
					) : (
						<div className="placeholder-state" style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)", width: "100%" }}>
							<p>No bids found for this product{selectedCountry?.code ? ` in ${selectedCountry.code}` : ""}.</p>
						</div>
					)}
				</>
			)}
			{/* Specification Gaps */}
			<div className="card_header">
				<h3 className="card_title my-0">Specification Gaps</h3>
			</div>
			{/* Specification Table */}
			<div className="specification-table w-full">
				<DataTable
					value={specification_gaps || []}
					tableStyle={{
						width: "100% !important",
						maxHeight: "200px",
					}}
					emptyMessage="No specification gaps found."
				>
					<Column field="supplier_name" header="Supplier"></Column>
					<Column
						field="compliance_status"
						header="Compliance Status"
						style={{ width: "190px" }}
						body={(row: any) => {
							let icon;
							switch (row.compliance_status) {
								case "Matches Spec":
									icon = <CheckIcon />;
									break;
								case "Partially Matches Spec":
									icon = <WarningIcon />;
									break;
								default:
									icon = <CrossIcon />;
							}
							return (
								<span className="flex items-center gap-2">
									{icon} {row.compliance_status}
								</span>
							);
						}}
					></Column>
					<Column
						field="key_issues"
						header="Key Issues"
						body={(row: any) => (
							<span>{row.key_issues?.join("; ") || "-"}</span>
						)}
					></Column>
				</DataTable>
			</div>
		</div>
	);
};

export default TenderAnalysis;
